import { formatUsd, isLiveSubscription, manageLabel, PAID_PLANS, PLAN_ORDER, PLANS, TIME_LIMIT_WARNING_MINUTES, type BillingState, type PaidPlanId, type PlanId } from '@calling/shared';
import { formatBytes, formatElapsed } from './format';

/** "3 November 2026", in the viewer's own conventions. */
export const longDate = (iso: string, locale?: string): string => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));

export interface PlanStatus {
  /** The plan's name, or what the account has instead of a plan. */
  name: string;
  /** A word or two for the badge next to the name; empty for none. */
  label: string;
  tone: 'good' | 'warn';
  /** One or two sentences: what is paid, and what happens next. */
  text: string;
  /** Whether the lines describing the plan's limits are worth showing. */
  showLimits: boolean;
}

/** The subscription the account is paying for right now, if any. */
export function paidSubscription(billing: BillingState): NonNullable<BillingState['subscription']> | null {
  return billing.subscription && isLiveSubscription(billing.subscription.status) ? billing.subscription : null;
}

export interface PlanOffers {
  /** Whether the plan cards are shown at all. */
  plans: boolean;
  /** The plans this account can buy now, on the payment page. */
  buy: PaidPlanId[];
  /** The plans someone already paying can switch to, on the billing page. */
  switchTo: PaidPlanId[];
  /** The card to mark "Your plan". */
  marked: PlanId | null;
  /** The account runs the service and is only walking through what customers see. */
  rehearsal: boolean;
}

/**
 * What the plan page offers an account. Buying happens on the website: the
 * apps show the plan and nothing to pay with. One subscription at a time:
 * someone already paying changes it on the billing page instead.
 *
 * The people who run the service are never limited, so with real payments
 * they are offered nothing. Where no real money moves (the payment sandbox,
 * or Stripe's test mode) they can still buy, to see what customers see.
 */
export function planOffers(billing: BillingState, native: boolean): PlanOffers {
  const none: PlanOffers = { plans: false, buy: [], switchTo: [], marked: billing.plan, rehearsal: false };
  if (!billing.enabled || native) return none;
  const paid = paidSubscription(billing);
  const others = (plan: PaidPlanId): PaidPlanId[] => PAID_PLANS.filter((p) => p !== plan);
  if (billing.source === 'operator') {
    if (billing.live) return none;
    return { plans: true, buy: paid ? [] : [...PAID_PLANS], switchTo: paid ? others(paid.plan) : [], marked: paid?.plan ?? null, rehearsal: true };
  }
  if (billing.source === 'subscription' && paid) return { plans: true, buy: [], switchTo: others(paid.plan), marked: billing.plan, rehearsal: false };
  // Free, or a plan given by hand: whatever is above it can be bought, unless something is being paid for already.
  const above = PAID_PLANS.filter((p) => PLAN_ORDER.indexOf(p) > PLAN_ORDER.indexOf(billing.plan ?? 'free'));
  return { plans: true, buy: paid ? [] : above, switchTo: [], marked: billing.plan, rehearsal: false };
}

type Paid = NonNullable<BillingState['subscription']>;

/** What a subscription costs, as Stripe charges it: someone who bought before the prices changed still pays what they paid then. */
export const paidPrice = (sub: Paid): string => `${formatUsd(sub.amount ?? PLANS[sub.plan].price[sub.interval])} a ${sub.interval}`;

// What the button that opens the billing page is called: the same rule the server's emails follow.
export { manageLabel };

/**
 * The sentences that differ between a server that takes payment through
 * Stripe and one whose payment sandbox only simulates it. Kept together so
 * that neither can claim what belongs to the other.
 */
export function paymentWords(sandbox: boolean): {
  pricingNote: string;
  paid: string;
  manageNote: string;
  intentNote: string;
  /** After a payment that did not go through: what happens by itself. */
  retry: string;
  /** And what puts it right, for someone who wants to keep the plan. */
  putRight: string;
} {
  return sandbox
    ? {
        pricingNote: 'Prices are in US dollars. This server is in payment sandbox mode: paying is simulated and nobody is charged.',
        paid: 'This was a sandbox payment: nobody was charged. The plan is active now.',
        manageNote: 'Switching plan and cancelling are on the sandbox’s billing page, which stands in for Stripe’s. Cancelling keeps the plan until the end of the period paid for.',
        intentNote: 'Payment is simulated on the sandbox’s page, and you come back here.',
        retry: 'The sandbox made it fail, and nothing is tried again here.',
        putRight: '“A later payment succeeds”, on the sandbox’s billing page, puts it right.',
      }
    : {
        pricingNote: 'Prices are in US dollars. Payment is by card, handled by Stripe.',
        paid: 'Thank you. Your payment went through and the plan is active now.',
        manageNote: 'Changing your card, switching plan, cancelling and your invoices are on Stripe’s billing page. Cancelling keeps the plan until the end of the period you paid for.',
        intentNote: 'You pay by card on Stripe’s page and come back here.',
        retry: 'Your card will be tried again.',
        putRight: 'Update your payment details to keep the plan.',
      };
}

/**
 * What to say about a plan that is paid for by an account that has every
 * feature, or a higher plan, for nothing. `rehearsal`: the account runs the
 * service and bought it only to see the journey. The sentence follows the
 * state of the plan, and names the button as the page labels it; in the
 * apps, where there is no such button, it names none.
 */
function paidSentence(paid: Paid, opts: { rehearsal: boolean; native: boolean; sandbox: boolean; locale?: string }): string {
  const what = `${PLANS[paid.plan].name} (${paidPrice(paid)})`;
  const opening = opts.rehearsal ? `You bought ${what} to try the journey.` : `You are still paying for ${what}.`;
  const button = `“${manageLabel(paid, opts.sandbox)}”`;
  const ends = paid.currentPeriodEnd ? `It ends on ${longDate(paid.currentPeriodEnd, opts.locale)} and will not be renewed.` : 'It will not be renewed.';
  if (paid.status === 'past_due') {
    const failed = opts.rehearsal
      ? 'Its last payment was made to fail, which is what a customer sees then.'
      : opts.sandbox
        ? 'Its last payment did not go through: the sandbox made it fail.'
        : 'Its last payment did not go through.';
    // Cancelled as well: there is nothing to be asked for, it ends either way.
    if (paid.cancelAtPeriodEnd) return `${opening} ${failed} ${ends}`;
    if (opts.native) return `${opening} ${failed}`;
    const next = opts.sandbox ? `Choose ${button}, then “A later payment succeeds”, to put it right.` : opts.rehearsal ? `Choose ${button} to go on.` : `Choose ${button} to pay, or to cancel it.`;
    return `${opening} ${failed} ${next}`;
  }
  if (paid.cancelAtPeriodEnd) return `${opening} ${ends}`;
  return `${opening}${opts.native ? '' : opts.rehearsal ? ` Choose ${button} to change or cancel it.` : ` Choose ${button} to cancel it.`}`;
}

/** The account's plan in words, for the plan page and the settings card. `native`: inside the Android or iOS app, where plans cannot be changed. */
export function planStatus(billing: BillingState, locale?: string, native = false): PlanStatus {
  if (!billing.enabled) return { name: 'Everything included', label: '', tone: 'good', text: 'This service does not sell plans. Every feature is part of your account.', showLimits: false };
  const sub = billing.subscription;
  // Someone who pays for a plan and then has one given to them is still paying until they cancel.
  const paying = paidSubscription(billing);
  if (billing.source === 'operator') {
    // Every feature, with the recording space the server gives (at least the largest plan's): not "no limits", since there is one.
    const noLimits = `You run this service, so your account has every feature of every plan: any number of people, meetings as long as you need, HD video, and ${formatBytes(billing.limits.storageBytes)} for recordings.`;
    if (billing.live) {
      return {
        name: 'Operator account',
        label: 'Every feature',
        tone: paying ? 'warn' : 'good',
        text: paying ? `${noLimits} That costs you nothing. ${paidSentence(paying, { rehearsal: false, native, sandbox: billing.sandbox, locale })}` : `${noLimits} Nothing is charged for it.`,
        showLimits: false,
      };
    }
    // No real money moves here, so the operator can walk the journey their customers will, and see each state as they would.
    if (paying) {
      return {
        name: 'Operator account',
        label: paying.status === 'past_due' ? 'Payment failed' : paying.cancelAtPeriodEnd ? 'Ending' : 'Every feature',
        tone: paying.status === 'past_due' || paying.cancelAtPeriodEnd ? 'warn' : 'good',
        text: `${noLimits} ${paidSentence(paying, { rehearsal: true, native, sandbox: billing.sandbox, locale })}`,
        showLimits: false,
      };
    }
    const how = billing.sandbox ? 'Payment on this server is simulated' : 'Payments here are in Stripe’s test mode';
    return {
      name: 'Operator account',
      label: 'Every feature',
      tone: 'good',
      text: native
        ? `${noLimits} Nothing is charged for it.`
        : `${noLimits} ${how}, so you can still buy a plan below to see what your customers see. It changes nothing for your account: to see the limits of the Free plan in a meeting, sign in with another account.`,
      showLimits: false,
    };
  }
  const plan = PLANS[billing.plan ?? 'free'];
  if (billing.source === 'granted') {
    return {
      name: plan.name,
      label: 'Given to you',
      tone: paying ? 'warn' : 'good',
      text: paying
        ? `Your account has been given the ${plan.name} plan. That costs you nothing. ${paidSentence(paying, { rehearsal: false, native, sandbox: billing.sandbox, locale })}`
        : `Your account has been given the ${plan.name} plan. Nothing is charged for it.`,
      showLimits: true,
    };
  }
  if (billing.source === 'subscription' && sub) {
    const price = paidPrice(sub);
    const until = sub.currentPeriodEnd ? longDate(sub.currentPeriodEnd, locale) : null;
    if (sub.status === 'past_due') {
      const words = paymentWords(billing.sandbox);
      // Cancelled as well: paying keeps the plan only until that date, so nothing promises more.
      // In the apps, where payment details cannot be changed, nothing is asked of the person.
      const next = !sub.cancelAtPeriodEnd ? (native ? '' : words.putRight) : until ? `The plan is also set to end on ${until} and will not be renewed.` : 'The plan is also set not to be renewed.';
      return { name: plan.name, label: 'Payment failed', tone: 'warn', text: [`Your last payment of ${price} did not go through.`, words.retry, next].filter(Boolean).join(' '), showLimits: true };
    }
    if (sub.cancelAtPeriodEnd) {
      // A plan given by hand is still there when the paid one ends.
      const after = billing.granted ? `your account goes back to the ${PLANS[billing.granted].name} plan it was given` : 'your account goes back to Free';
      return {
        name: plan.name,
        label: 'Ending',
        tone: 'warn',
        text: until ? `Your plan ends on ${until} and will not be renewed. After that ${after}.` : `Your plan will not be renewed. When the period you paid for ends, ${after}.`,
        showLimits: true,
      };
    }
    return { name: plan.name, label: 'Active', tone: 'good', text: until ? `${price}. Renews on ${until}.` : `${price}.`, showLimits: true };
  }
  return { name: plan.name, label: '', tone: 'good', text: 'Free for as long as you like.', showLimits: true };
}

/** "up to 4 people and 40 minutes": the limits of a meeting, for one sentence. Empty when there are none. */
export function meetingLimitsText(limits: { maxParticipants: number | null; maxMeetingMinutes: number | null }): string {
  const parts = [
    ...(limits.maxParticipants !== null ? [`${limits.maxParticipants} people`] : []),
    ...(limits.maxMeetingMinutes !== null ? [`${limits.maxMeetingMinutes} minutes`] : []),
  ];
  return parts.length > 0 ? `up to ${parts.join(' and ')}` : '';
}

/**
 * What the call screen says about a meeting with a time limit. Minutes while
 * there is plenty of time; minutes and seconds, and `soon`, near the end.
 */
export function timeLeft(endsAt: number, now: number, soonMs = TIME_LIMIT_WARNING_MINUTES * 60_000): { text: string; soon: boolean } {
  const ms = Math.max(0, endsAt - now);
  if (ms <= soonMs) return { text: `Ends in ${formatElapsed(ms)}`, soon: true };
  return { text: `Ends in ${Math.ceil(ms / 60_000)} min`, soon: false };
}
